import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Award, Users } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunityLeaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/community/leaderboard`);
      setLeaderboard(response.data.leaderboard);
    } catch (err) {
      setError('Failed to load leaderboard');
      console.error('Leaderboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Award className="w-6 h-6 text-orange-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-gray-600 font-bold">#{rank}</span>;
    }
  };

  const getPointsColor = (score) => {
    if (score >= 100) return 'text-purple-600';
    if (score >= 50) return 'text-blue-600';
    if (score >= 25) return 'text-green-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-4 mb-4">
              <div className="w-8 h-8 bg-gray-200 rounded"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
              <div className="h-6 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center mb-6">
        <Trophy className="w-6 h-6 text-yellow-500 mr-2" />
        <h2 className="text-xl font-bold text-gray-800">Community Leaders</h2>
      </div>

      {leaderboard.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium mb-2">Be the First Contributor!</p>
          <p className="text-sm">Start earning points by:</p>
          <ul className="text-sm mt-2 space-y-1">
            <li>• Suggesting new venues (+10 points)</li>
            <li>• Writing venue reviews (+5 points)</li>
            <li>• Uploading accessibility photos (+3 points)</li>
            <li>• Sharing helpful tips (+2 points)</li>
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          {leaderboard.map((entry) => (
            <div
              key={entry.rank}
              className={`flex items-center p-4 rounded-lg border ${
                entry.rank <= 3 
                  ? 'border-yellow-200 bg-yellow-50' 
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center w-10 h-10 mr-4">
                {getRankIcon(entry.rank)}
              </div>
              
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{entry.user_name}</h3>
                <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                  <span>🏢 {entry.contributions.venue_submissions} venues</span>
                  <span>⭐ {entry.contributions.ratings} reviews</span>
                  <span>📸 {entry.contributions.photos} photos</span>
                  <span>💡 {entry.contributions.tips} tips</span>
                </div>
              </div>
              
              <div className="text-right">
                <div className={`text-lg font-bold ${getPointsColor(entry.contribution_score)}`}>
                  {entry.contribution_score}
                </div>
                <div className="text-xs text-gray-500">points</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">How to Earn Points</h3>
        <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
          <div>• Venue submission: +10 pts</div>
          <div>• Write review: +5 pts</div>
          <div>• Upload photo: +3 pts</div>
          <div>• Share tip: +2 pts</div>
        </div>
      </div>
    </div>
  );
};

export default CommunityLeaderboard;